import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../context/TenantContext";
import umService from "../../services/university-admin/umService";
import AppButton from "../../components/ui/AppButton";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMUserList = ({ onEdit, onView, onStatusEdit, onDelete, refreshTrigger }) => {
  const { t } = useTranslation(["users", "common"]);
  const { userRole, currentTenantId, universities } = useTenant();
  const { can } = useUniAdminAuth();
  // user:update / user:delete have no catalog key (these actions are mock-only,
  // not backed by a real university-admin endpoint), so they're left ungated.
  const canManageStatus = can(PERMISSIONS.user.toggleActive) || can(PERMISSIONS.user.toggleBan);
  const canFilterByCampus = can(PERMISSIONS.campus.list);

  const [users, setUsers] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    campusId: "",
    universityId: "",
    emailVerified: "",
    banned: "",
    isActive: "",
  });

  useEffect(() => {
    if (!canFilterByCampus) return;
    setFiltersLoading(true);
    umService.getAllCampuses({ userRole, currentTenantId }).then((res) => {
      if (res.success) setCampuses(res.data);
      setFiltersLoading(false);
    });
  }, [userRole, currentTenantId, canFilterByCampus]);

  useEffect(() => {
    setLoading(true);
    umService
      .getUsersDemo({ userRole, currentTenantId, page: currentPage, size: pageSize, filters })
      .then((res) => {
        if (res.success) {
          setUsers(res.data.content);
          setTotalPages(res.data.totalPages);
          setTotalElements(res.data.totalElements);
        }
      })
      .finally(() => setLoading(false));
  }, [userRole, currentTenantId, currentPage, pageSize, filters, refreshTrigger]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(0);
  };

  const hasActiveFilters = Object.values(filters).some((value) => value !== "");

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <AppButton
          key={i}
          variant={i === currentPage ? "primary" : "outline"}
          className="btn-sm mx-1"
          onClick={() => setCurrentPage(i)}
        >
          {i + 1}
        </AppButton>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          {t("common:pagination.showingEntries", {
            start: currentPage * pageSize + 1,
            end: Math.min((currentPage + 1) * pageSize, totalElements),
            total: totalElements,
          })}
        </div>
        <div className="d-flex align-items-center">
          <AppButton variant="outline" className="btn-sm me-2" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>
            {t("common:buttons.previous")}
          </AppButton>
          {pages}
          <AppButton variant="outline" className="btn-sm ms-2" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>
            {t("common:buttons.next")}
          </AppButton>
        </div>
      </div>
    );
  };

  return (
    <div className="user-list">
      <ListToolbar
        title={t("users:title")}
        subtitle={t("users:list.found", { count: totalElements })}
        filterBadges={
          hasActiveFilters ? (
            <>
              {filters.universityId && <span className="badge bg-primary">{t("users:filters.university")}: {universities.find((item) => String(item.id) === filters.universityId)?.nameLocal}</span>}
              {filters.campusId && <span className="badge bg-info">{t("users:filters.campus")}: {campuses.find((item) => String(item.id) === filters.campusId)?.nameLocal}</span>}
              {filters.emailVerified !== "" && <span className="badge bg-warning">{t("users:table.email")}: {filters.emailVerified === "true" ? t("users:filters.verified") : t("users:filters.unverified")}</span>}
              {filters.isActive !== "" && <span className="badge bg-success">{t("users:filters.activeStatus")}: {filters.isActive === "true" ? t("common:status.active") : t("common:status.inactive")}</span>}
              {filters.banned !== "" && <span className="badge bg-danger">{t("users:filters.banStatus")}: {filters.banned === "true" ? t("users:filters.banned") : t("users:filters.notBanned")}</span>}
            </>
          ) : null
        }
      >
        <div className="row g-3">
          <div className="col-md-6">
            <div className="row">
              <div className="col-md-6">
                <label className="form-label fw-medium">{t("users:filters.university")}</label>
                <select className="form-select form-select-sm" value={filters.universityId} onChange={(e) => handleFilterChange("universityId", e.target.value)} disabled={filtersLoading}>
                  <option value="">{t("users:filters.allUniversities")}</option>
                  {universities.map((university) => (
                    <option key={university.id} value={university.id}>{university.nameLocal || university.nameEn}</option>
                  ))}
                </select>
              </div>
              {canFilterByCampus && (
                <div className="col-md-6">
                  <label className="form-label fw-medium">{t("users:filters.campus")}</label>
                  <select className="form-select form-select-sm" value={filters.campusId} onChange={(e) => handleFilterChange("campusId", e.target.value)} disabled={filtersLoading}>
                    <option value="">{t("users:filters.allCampuses")}</option>
                    {campuses.map((campus) => (
                      <option key={campus.id} value={campus.id}>{campus.nameLocal || campus.nameEn}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="row">
              <div className="col-md-4">
                <label className="form-label fw-medium">{t("users:filters.emailStatus")}</label>
                <select className="form-select form-select-sm" value={filters.emailVerified} onChange={(e) => handleFilterChange("emailVerified", e.target.value)}>
                  <option value="">{t("common:common.all")}</option>
                  <option value="true">{t("users:filters.verified")}</option>
                  <option value="false">{t("users:filters.unverified")}</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-medium">{t("users:filters.banStatus")}</label>
                <select className="form-select form-select-sm" value={filters.banned} onChange={(e) => handleFilterChange("banned", e.target.value)}>
                  <option value="">{t("common:common.all")}</option>
                  <option value="true">{t("users:filters.banned")}</option>
                  <option value="false">{t("users:filters.notBanned")}</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-medium">{t("users:filters.activeStatus")}</label>
                <select className="form-select form-select-sm" value={filters.isActive} onChange={(e) => handleFilterChange("isActive", e.target.value)}>
                  <option value="">{t("common:common.all")}</option>
                  <option value="true">{t("common:status.active")}</option>
                  <option value="false">{t("common:status.inactive")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">{t("common:pagination.showing")}:</label>
              <select className="form-select form-select-sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setCurrentPage(0); }} style={{ width: "auto" }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="col-auto d-flex align-items-end">
              <AppButton variant="outline" className="btn-sm" onClick={() => { setFilters({ campusId: "", universityId: "", emailVerified: "", banned: "", isActive: "" }); setCurrentPage(0); }}>
                <i className="fas fa-times me-2"></i>{t("common:buttons.clearFilters")}
              </AppButton>
            </div>
          )}
        </div>
      </ListToolbar>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t("common:actions.loading")}</span></div>
              <p className="text-muted mt-2">{t("users:list.loading")}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-users text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">{t("users:list.noFound")}</h5>
              <p className="text-muted">{hasActiveFilters ? t("users:list.tryAdjustFilters") : t("users:list.noneRegistered")}</p>
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th className="ps-4 user-column">{t("users:table.user")}</th>
                  <th className="campus-column">{t("users:table.campus")}</th>
                  <th className="text-center status-column">{t("users:table.role")}</th>
                  <th className="text-center status-column">{t("users:table.active")}</th>
                  <th className="text-center status-column">{t("users:table.banned")}</th>
                  <th className="text-center status-column">{t("users:table.email")}</th>
                  <th className="text-center pe-4">{t("common:table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-bottom">
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        <div className="user-avatar rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center me-3" style={{ width: "45px", height: "45px" }}>
                          <i className="fas fa-user text-primary"></i>
                        </div>
                        <div>
                          <div className="fw-bold text-dark">{user.fullName}</div>
                          <small className="text-muted">ID: {user.id}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="campus-info">
                        <div className="campus-name">{user.campusName || "N/A"}</div>
                        <div className="faculty-name">{user.facultyName || "N/A"}</div>
                      </div>
                    </td>
                    <td className="text-center">
                      {user.role === "ROLE_UNIVERSITY_ADMIN" ? (
                        <span className="badge bg-danger"><i className="fas fa-crown me-1"></i>{t("users:roles.ROLE_UNIVERSITY_ADMIN")}</span>
                      ) : user.role === "ROLE_ADMIN" ? (
                        <span className="badge bg-danger"><i className="fas fa-crown me-1"></i>{t("users:roles.ROLE_ADMIN")}</span>
                      ) : user.role === "ROLE_MODERATOR" ? (
                        <span className="badge bg-warning"><i className="fas fa-shield me-1"></i>{t("users:roles.ROLE_MODERATOR")}</span>
                      ) : (
                        <span className="badge bg-info"><i className="fas fa-user me-1"></i>{t("users:roles.ROLE_USER")}</span>
                      )}
                    </td>
                    <td className="text-center">{user.active ? <i className="fas fa-check-circle text-success status-icon"></i> : <i className="fas fa-times-circle text-danger status-icon"></i>}</td>
                    <td className="text-center">{user.banned ? <i className="fas fa-ban text-danger status-icon"></i> : <i className="fas fa-check-circle text-success status-icon"></i>}</td>
                    <td className="text-center">{user.emailVerified ? <i className="fas fa-envelope-circle-check text-success status-icon"></i> : <i className="fas fa-envelope text-warning status-icon"></i>}</td>
                    <td className="text-center pe-4">
                      <div className="btn-group" role="group">
                        {can(PERMISSIONS.user.get) && (
                          <AppButton variant="outline" className="btn-sm text-info border-info" onClick={() => onView(user)} title={t("common:buttons.viewDetails")}><i className="fas fa-eye"></i></AppButton>
                        )}
                        <AppButton variant="outline" className="btn-sm text-primary border-primary" onClick={() => onEdit(user)} title={t("common:buttons.edit")}><i className="fas fa-edit"></i></AppButton>
                        {canManageStatus && (
                          <AppButton variant="outline" className="btn-sm text-warning border-warning" onClick={() => onStatusEdit(user)} title={t("users:manageStatus")}><i className="fas fa-user-shield"></i></AppButton>
                        )}
                        <AppButton variant="outline" className="btn-sm text-danger border-danger" onClick={() => onDelete(user)} title={t("common:buttons.delete")}><i className="fas fa-trash"></i></AppButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>
      </div>

      {renderPagination()}
    </div>
  );
};

export default UMUserList;
